import React from 'react'
import { observer } from 'mobx-react-lite'
import { Link, useLocation } from 'react-router-dom'
import { Layout } from '../Layout'
import { FloatingToolbar } from '../promotions/FloatingToolbar'
import { Params } from '../../packages/blueprint-components/src/useTableHooks'
import { isNavV2Enabled } from '../../util/nav-version'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface BreadcrumbItem {
    /** Text or JSX rendered in the crumb */
    label: React.ReactNode
    /** If provided the crumb is wrapped in a react-router <Link> */
    href?: string
}

export interface NewPageLayoutProps {
    /** Called when the user clicks the Classic toggle in the floating toolbar */
    onSwitchToClassic?: () => void
    /** Forwarded to FloatingToolbar for the column-picker, etc. */
    toolbarParams?: Params
    /** Breadcrumb trail shown above the title in muted 13px text */
    breadcrumbs?: BreadcrumbItem[]
    /** The main h1 heading */
    title: React.ReactNode
    /** Anything rendered inline directly after the h1 (e.g. a help icon) */
    titleExtra?: React.ReactNode
    /** Content pinned to the far right of the title row via ml-auto */
    rightActions?: React.ReactNode
    /**
     * Tailwind classes applied to the header wrapper div.
     * Default: 'pt-8 mb-8'
     */
    headerClassName?: string
    flatNavV2?: boolean
    children: React.ReactNode
}

// ── Component ─────────────────────────────────────────────────────────────────

export const NewPageLayout = observer(
    ({
        onSwitchToClassic,
        toolbarParams = {},
        breadcrumbs = [],
        title,
        titleExtra,
        rightActions,
        headerClassName = 'pt-8 mb-8',
        flatNavV2 = false,
        children,
    }: NewPageLayoutProps) => {
        const { search } = useLocation()
        const useFlatNavV2 = flatNavV2 && isNavV2Enabled(search)
        const toolbar = (
            <FloatingToolbar
                onSwitchToClassic={onSwitchToClassic}
                activeLabel="New"
                params={toolbarParams}
            />
        )

        if (useFlatNavV2) {
            return (
                <Layout
                    breadcrumbs={breadcrumbs.map((crumb) => ({
                        text: crumb.label,
                        href: crumb.href,
                    }))}
                    primary={
                        <span className="inline-flex items-center gap-2">
                            {title}
                            {titleExtra}
                        </span>
                    }
                    rightActions={rightActions}
                    params={toolbarParams}
                    className="bg-white"
                    layoutClassName="!p-0 md:!p-0 bg-white"
                >
                    {children}
                </Layout>
            )
        }

        return (
            <Layout nested layoutClassName="px-3 md:px-6 pb-6">
                {toolbar}
                <div className="max-w-5xl mx-auto">
                    <div className={headerClassName}>
                        {/* Breadcrumbs */}
                        {breadcrumbs.length > 0 && (
                            <div className="flex items-center gap-1.5 text-[13px] text-[#94A3B8] mb-3 select-none">
                                {breadcrumbs.map((crumb, i) => (
                                    <React.Fragment key={i}>
                                        {i > 0 && <span className="text-[#CBD5E1]">/</span>}
                                        {crumb.href ? (
                                            <Link
                                                to={crumb.href}
                                                className="no-underline hover:text-[#0E2947] transition-colors"
                                            >
                                                {crumb.label}
                                            </Link>
                                        ) : (
                                            <span>{crumb.label}</span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        )}

                        {/* Title row */}
                        <div className="flex items-center gap-2">
                            <h1 className="text-[40px] font-bold tracking-[-0.04em] leading-none text-[#0E2947] m-0">
                                {title}
                            </h1>
                            {titleExtra}
                            {rightActions && (
                                <div className="ml-auto flex items-center gap-2">
                                    {rightActions}
                                </div>
                            )}
                        </div>
                    </div>

                    {children}
                </div>
            </Layout>
        )
    },
)
